import type { ComponentType } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { cn } from '@/utils';
import { Crown, Gamepad2, Home, MessageCircleMore, ScrollText, UserCircle2 } from 'lucide-react';
import { useChatStore } from '@/stores/chat-store';
import { useAuth } from '@/hooks';
import { isNavFeatureEnabled } from '@/constants/navFeatures';
import { NftIcon } from '@/components/icons';
import { useNftAttention } from '@/hooks/useNftAttention';

type NavItem = {
	key: 'home' | 'games' | 'quests' | 'vip' | 'nft' | 'profile';
	to: string;
	label: string;
	icon: ComponentType<{ className?: string }>;
	match?: (pathname: string) => boolean;
};

const navItems: NavItem[] = [
	{
		key: 'home',
		to: '/',
		label: 'Home',
		icon: Home,
		match: (pathname) => pathname === '/',
	},
	{
		key: 'games',
		to: '/games',
		label: 'Games',
		icon: Gamepad2,
		match: (pathname) => pathname.startsWith('/games'),
	},
	{
		key: 'quests',
		to: '/quests',
		label: 'Quests',
		icon: ScrollText,
		match: (pathname) => pathname.startsWith('/quests'),
	},
	{
		key: 'vip',
		to: '/vip',
		label: 'VIP',
		icon: Crown,
		match: (pathname) => pathname.startsWith('/vip'),
	},
	{
		key: 'nft',
		to: '/nft',
		label: 'NFT',
		icon: NftIcon,
		match: (pathname) => pathname.startsWith('/nft'),
	},
	{
		key: 'profile',
		to: '/profile',
		label: 'Profile',
		icon: UserCircle2,
		match: (pathname) => pathname.startsWith('/profile'),
	},
];

export function MobileBottomNav() {
	const { isLogged } = useAuth();
	const { location } = useRouterState();
	const pathname = location.pathname;
	const toggleChat = useChatStore((s) => s.toggle);
	const isChatOpen = useChatStore((s) => s.isOpen);
	const unreadCount = useChatStore((s) => Object.values(s.unreadByChannel ?? {}).reduce((total, value) => total + (value ?? 0), 0));
	const unreadLabel = unreadCount > 99 ? '99+' : unreadCount ? `${unreadCount}` : null;
	const unreadKey = unreadLabel ?? '0';
	const { shouldShowNftBadge, dismissNftBadge } = useNftAttention();
	const availableNavItems = navItems.filter((item) => {
		if (item.key === 'profile') {
			return isLogged;
		}
		if (item.key === 'quests') {
			return isNavFeatureEnabled('quests');
		}
		if (item.key === 'vip') {
			return isNavFeatureEnabled('vip');
		}
		return true;
	});

	return (
		<nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 pb-[env(safe-area-inset-bottom)] pt-2 shadow-[0_-8px_20px_-15px_rgba(7,20,38,0.25)] backdrop-blur md:hidden">
			<div className="mx-auto flex max-w-4xl items-center justify-around sm:px-3">
				{availableNavItems.map((item) => {
					const Icon = item.icon;
					const isActive = item.match ? item.match(pathname) : pathname === item.to;
					const showNftBadge = item.key === 'nft' && shouldShowNftBadge;

					return (
						<Link
							key={item.to}
							to={item.to}
							className={cn(
								'flex flex-1 flex-col items-center gap-1 rounded-xl px-2 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors',
								isActive ? 'text-primary' : 'text-muted-foreground',
							)}
							activeOptions={{ exact: item.to === '/' }}
							preload="intent"
							onClick={() => {
								if (item.key === 'nft') {
									dismissNftBadge();
								}
							}}
						>
							<span className="relative flex">
								<Icon
									className={cn('h-5 w-5', isActive ? 'text-primary drop-shadow-sm drop-shadow-primary' : 'text-muted-foreground/80')}
									aria-hidden
								/>
								{showNftBadge ? (
									<span className="pointer-events-none absolute -right-1.5 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-black text-white shadow-sm">
										!
									</span>
								) : null}
							</span>
							<span>{item.label}</span>
						</Link>
					);
				})}
				<button
					type="button"
					onClick={toggleChat}
					aria-pressed={isChatOpen}
					className={cn(
						'relative flex flex-1 flex-col items-center gap-1 rounded-xl px-2 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors',
						isChatOpen ? 'text-primary' : 'text-muted-foreground',
					)}
				>
					<span className="relative flex">
						<MessageCircleMore
							className={cn('h-5 w-5', isChatOpen ? 'text-primary drop-shadow-sm drop-shadow-primary' : 'text-muted-foreground/80')}
							aria-hidden
						/>
						{unreadLabel ? (
							<span
								key={unreadKey}
								className="pointer-events-none absolute -right-2 -top-2 inline-flex min-w-5 items-center justify-center rounded-full bg-destructive/90 px-1.5 text-xs font-semibold text-inverted-foreground shadow"
							>
								{unreadLabel}
							</span>
						) : null}
					</span>
					<span>Chat</span>
				</button>
			</div>
		</nav>
	);
}

export default MobileBottomNav;
